/**
 * Figures out how to display period names in places where space is constrained,
 * such as under the scorecards overview historical performance chart, or
 * on the scorecards metrics report.
 *
 * The return format of this is a little odd because this started out just
 * for the historical performance chart and was then extracted out.  We could
 * consider changing it later if we get a third example of how this will be
 * used.
 */
import calendarInfo, {
	getYearMatches,
	NO_YEARS,
	YEARS_BY_DATE,
	YEARS_BY_LABEL
} from 'scoreboard/components/calendar-info'

import _ from 'lodash'
import moment from 'moment'
import i18n from 'scoreboard/components/i18n'

function domain(periods) {
	if (_.isEmpty(periods)) {
		return []
	}

	var info = calendarInfo(periods[0].calendar)

	var domain = _.map(periods, mapPeriod.bind(null, info))
	return domain
}

function singleDomain (period) {
	var info = calendarInfo(period.calendar)
	return mapPeriod(info, period)
}

function mapPeriod (info, period) {
	if (info.style === YEARS_BY_LABEL) {
		return domainByLabel(info, period)
	}
	if (info.style === YEARS_BY_DATE) {
		return domainByDate(info, period)
	}
	if (info.style === NO_YEARS) {
		return domainNoYears(info, period)
	}

	// shouldn't reach here, but just in case
	return {
		title: period.label
	}
}

// e.g. monthly, quarterly
function domainByLabel(info, period) {
	var title = info.labelsByPeriod[period.id]
	var end = moment(period.end)
	let subtitle = end.format('YYYY')

	// convert e.g. January -> Jan
	const lowerPeriodLabel = title.toLowerCase()
	const monthIndex = monthLabelToIndex[lowerPeriodLabel]
	if (!_.isNil(monthIndex)) {
		var m = moment(period.endDate)
		m.month(monthIndex)
		title = m.format('MMM')
	} else {
		let yearMatches = getYearMatches(period.label)
		// #20458 if a 4 digit year was included in the period name, use it
		// for example, Q1 FY 2019
		if (yearMatches && yearMatches.length == 1) {
			subtitle = yearMatches[0]
		}
		// converts e.g. Fiscal Quarterly 1, 2014 to Q1
		var match = quarterlyRegexp().exec(period.label)
		if (match) {
			title = i18n.get('admin.calendar.quarterly.periodlabel.short', match[1])
		}
	}

	return {
		title,
		subtitle,
		id: period.id
	}
}

// e.g. weekly
function domainByDate(info, period) {
	if (period.calendar.avgPeriodSize <= 10) {
		var end = moment(period.end)
		return {
			id: period.id,
			title: end.format('MMM D'),
			subtitle: end.format('YYYY')
		}
	} else {
		return { id: period.id, title: period.label }
	}
}

function domainNoYears(info, period) {
	var label = period.label
	var match = fiscalYearlyRegexp().exec(period.label)
	if (match) {
		label = match[1]
	}

	return { id: period.id, title: label }
}

//returns e.g. /Quarterly (\d*), \d*/
//define as a function so i18n doesn't get hit before the app is ready
var quarterlyRegexp = _.memoize(function() {
	var quarterlyRegexpStr = i18n.get('admin.calendar.quarterly.periodlabel', '(\\d*)', '\\d*')
	return new RegExp(quarterlyRegexpStr)
})

//returns e.g. /Fiscal (\d*)*/
//define as a function so i18n doesn't get hit before the app is ready
var fiscalYearlyRegexp = _.memoize(function() {
	var str = i18n.get('admin.calendar.usgovernment.fiscal', '(\\d*)')
	return new RegExp(str)
})

let monthLabelToIndex = {}

function initialize() {
	var m = moment()
	m.locale('en')

	monthLabelToIndex = {}
	_.times(12, i => {
		monthLabelToIndex[m.month(i).format('MMMM').toLowerCase()] = i
	})

	return monthLabelToIndex
}

initialize()

const single = singleDomain
_.extend(domain, { single, initialize })
export default domain
export { single }
export { initialize }

domain.__filename__ = 'components/period-titles.js'
